import axios from "axios";
import { Check, RefreshCw, Server, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Spinner,
} from "react-bootstrap";

const ConfigureTestServer = () => {
  const [serverStatus, setServerStatus] = useState("checking");
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [error, setError] = useState(null);

  const checkServerStatus = React.useCallback(async () => {
    setServerStatus("checking");
    try {
      await fetch("https://test.plasticise.com.au", {
        method: "GET",
        mode: "no-cors", // Since we're just checking availability
        cache: "no-cache",
      });

      // Since no-cors doesn't allow checking status, we'll assume it's online if we get here
      setServerStatus("online");
    } catch (error) {
      console.error("Error checking server status:", error);
      setServerStatus("offline");
    }
  }, []); // Removed serverStatus from dependency array to fix circular dependency

  useEffect(() => {
    checkServerStatus();
  }, [checkServerStatus]);

  const updateTestDatabase = async () => {
    setIsUpdating(true);
    setUpdateResult(null);
    setError(null);

    try {
      await axios.post(
        "https://api.github.com/repos/Plasticise-app/plasticise/actions/workflows/update-test-db.yaml/dispatches",
        { ref: "testing" },
        {
          headers: {
            Authorization: `token ${process.env.REACT_APP_PLASTICISE_ACTIONS_TOKEN}`,
            Accept: "application/vnd.github.v3+json",
          },
        }
      );

      // GitHub API returns 204 No Content on successful workflow dispatch
      setUpdateResult({
        message:
          "Database update workflow triggered successfully. You'll receive an email when complete.",
      });
    } catch (error) {
      console.error("Error triggering database update:", error);
      setError(
        error.response?.data?.message ||
          "Failed to trigger database update. Please try again later."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  const renderStatusBadge = () => {
    switch (serverStatus) {
      case "online":
        return (
          <Badge bg="success" className="d-flex align-items-center gap-1">
            <Check size={14} /> Online
          </Badge>
        );
      case "offline":
        return (
          <Badge bg="danger" className="d-flex align-items-center gap-1">
            <X size={14} /> Offline
          </Badge>
        );
      default:
        return (
          <Badge bg="secondary" className="d-flex align-items-center gap-1">
            <Spinner animation="border" size="sm" /> Checking
          </Badge>
        );
    }
  };

  return (
    <Container fluid className="px-2 py-4">
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">Configure Test Server</h2>
            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={checkServerStatus}
                disabled={serverStatus === "checking"}
              >
                <RefreshCw size={16} className="me-1" />
                Refresh Status
              </Button>
              {renderStatusBadge()}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-3">
              <Server size={24} />
              <h4 className="mb-0">Test Server Management</h4>
            </div>
            <p>
              This page allows you to manage the test server environment at{" "}
              <a
                href="https://test.plasticise.com.au"
                target="_blank"
                rel="noopener noreferrer"
              >
                test.plasticise.com.au
              </a>
            </p>
          </div>

          <Card className="mb-4">
            <Card.Header style={{ backgroundColor: "#f8f9fa" }}>
              <h5 className="mb-0">Update Test Database</h5>
            </Card.Header>
            <Card.Body>
              <Alert variant="info">
                <Alert.Heading>Important Information</Alert.Heading>
                <p>
                  Clicking the button below will replace all data in the test
                  database with the latest snapshot from the production
                  database. In the testing environment, non test user data will
                  be changed to prevent accidental notifications or emails from
                  being sent. This process takes approximately 3-5 minutes.
                </p>
                <p>
                  user IDs can be viewed{" "}
                  <a href="/admin/api/customuser/" target="_blank">
                    here
                  </a>
                </p>
                <hr />
                <p className="mb-0">
                  You will receive an email notification when the update is
                  complete.
                </p>
              </Alert>

              {error && (
                <Alert variant="danger" className="mt-3">
                  <Alert.Heading>Error</Alert.Heading>
                  <p>{error}</p>
                </Alert>
              )}

              {updateResult && (
                <Alert variant="success" className="mt-3">
                  <Alert.Heading>Success</Alert.Heading>
                  <p>The database update has been triggered successfully.</p>
                  <p className="mb-0">
                    {updateResult.message ||
                      "You will receive an email when the process is complete."}
                  </p>
                </Alert>
              )}

              <div className="d-grid gap-2 mt-3">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={updateTestDatabase}
                  disabled={isUpdating || serverStatus !== "online"}
                  style={{ backgroundColor: "#e22e78", borderColor: "#e22e78" }}
                >
                  {isUpdating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Triggering Update...
                    </>
                  ) : (
                    "Update Test Database"
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ConfigureTestServer;
